/**
 * ========================
 * CALL CENTRE ("CALL CAMPAIGNS") DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Call Centre Management console. Labelled "Call campaigns" so it is not
 * confused with Communications campaigns.
 * Sections:
 *  1. Overview: Dashboard
 *  2. Calling: Campaigns, Contacts, Do not call, Detail changes
 *  3. Follow-up: Journey, Handoffs
 *  4. People and results: Callers, Reports
 *  5. Administration: Settings, Phone system (Stage 3: connection, desk stations, call records)
 *
 * Visibility comes from CALLCENTRE_PAGE_ACCESS in useCallCentrePermissions, the same predicates the
 * routes hand to CallCentreGate, so an item shows exactly when its page would open. A section shows
 * when any of its items does.
 */

import {
  IconDashboard,
  IconPhoneCall,
  IconAddressBook,
  IconPhoneOff,
  IconListDetails,
  IconRoute,
  IconArrowForwardUp,
  IconUsers,
  IconChartBar,
  IconSettings,
  IconDeviceLandlinePhone,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { CALLCENTRE_PAGE_ACCESS as page, callCentreMenuVisibility as when } from '../../hooks/callcentre/useCallCentrePermissions';

class CallCentreMenuProvider implements DomainMenuProvider {
  domainId = 'callcentre';

  getMenuConfig() {
    const sections: MenuSection[] = [
      {
        id: 'callcentre-overview',
        label: 'Overview',
        visible: when(page.dashboard),
        items: [{ id: 'callcentre-dashboard', label: 'Dashboard', path: '/app/callcentre', icon: <IconDashboard size={16} />, visible: when(page.dashboard) }],
      },
      {
        id: 'callcentre-calling',
        label: 'Calling',
        visible: when(page.campaigns, page.contacts, page.doNotCall, page.detailChanges),
        items: [
          { id: 'callcentre-campaigns', label: 'Campaigns', path: '/app/callcentre/campaigns', icon: <IconPhoneCall size={16} />, visible: when(page.campaigns) },
          { id: 'callcentre-contacts', label: 'Contacts', path: '/app/callcentre/contacts', icon: <IconAddressBook size={16} />, visible: when(page.contacts) },
          { id: 'callcentre-do-not-call', label: 'Do not call', path: '/app/callcentre/do-not-call', icon: <IconPhoneOff size={16} />, visible: when(page.doNotCall) },
          { id: 'callcentre-detail-changes', label: 'Detail changes', path: '/app/callcentre/detail-changes', icon: <IconListDetails size={16} />, visible: when(page.detailChanges) },
        ],
      },
      {
        id: 'callcentre-follow-up',
        label: 'Follow-up',
        visible: when(page.journey, page.handoffs),
        items: [
          { id: 'callcentre-journey', label: 'Journey', path: '/app/callcentre/journey', icon: <IconRoute size={16} />, visible: when(page.journey) },
          { id: 'callcentre-handoffs', label: 'Handoffs', path: '/app/callcentre/handoffs', icon: <IconArrowForwardUp size={16} />, visible: when(page.handoffs) },
        ],
      },
      {
        id: 'callcentre-people',
        label: 'People and results',
        visible: when(page.callers, page.reports),
        items: [
          { id: 'callcentre-callers', label: 'Callers', path: '/app/callcentre/callers', icon: <IconUsers size={16} />, visible: when(page.callers) },
          { id: 'callcentre-reports', label: 'Reports', path: '/app/callcentre/reports', icon: <IconChartBar size={16} />, visible: when(page.reports) },
        ],
      },
      {
        id: 'callcentre-administration',
        label: 'Administration',
        visible: when(page.settings, page.telephony),
        items: [
          { id: 'callcentre-settings', label: 'Settings', path: '/app/callcentre/settings', icon: <IconSettings size={16} />, visible: when(page.settings) },
          { id: 'callcentre-telephony', label: 'Phone system', path: '/app/callcentre/telephony', icon: <IconDeviceLandlinePhone size={16} />, visible: when(page.telephony) },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Call campaigns (call centre) menu',
      },
    };
  }
}

export const callCentreMenuProvider = new CallCentreMenuProvider();
